@if (loading()) {
  <div class="page-container">
    <div class="detail-loading">Cargando empleado...</div>
  </div>
} @else if (empleado(); as emp) {
  <div class="page-container">
    <!-- Page Header -->
    <intelishs-page-header
      [title]="emp.nombreCompleto"
      [subtitle]="emp.idInterno + ' — Expediente del empleado'"
    />
    <!-- Un aviso, no un tag: es una advertencia sobre el registro completo, no
         una etiqueta de clasificación. -->
    @if (provisional()) {
      <div class="aviso aviso--warning">
        <nz-icon nzType="warning" />
        <span>Empleado provisional — pendiente de alta en base de datos por el DBA</span>
      </div>
    }

    <!-- Estado a la izquierda, acciones a la derecha, y la barra viaja con el
         scroll: el expediente son 68 campos, así que salir o editar no debería
         obligar a volver hasta arriba.
         El estado vivía en un `<div>` suelto debajo de los botones, sin
         etiqueta ni contexto —de ahí que se leyera como información flotante—.
         Aquí pertenece a la barra del expediente, y va como punto de color en
         vez de pastilla, igual que en el resto del sistema. -->
    <intelishs-page-actions variant="bar" align="split">
      <intelishs-status-text [tone]="emp.activo ? 'ok' : 'error'">
        {{ emp.activo ? 'Activo' : 'Inactivo' }}
      </intelishs-status-text>

      <span>
        <button nz-button class="sinay-outline-reset" (click)="volver()">
          <nz-icon nzType="arrow-left" />
          Volver
        </button>
        <button nz-button class="btn-add" [disabled]="provisional()" (click)="editar()">
          <nz-icon nzType="edit" />
          Editar
        </button>
      </span>
    </intelishs-page-actions>

    <!-- ═══ Información Personal ═══ -->
    <div class="form-section">
      <intelishs-section-header title="Información Personal" />
      <div class="detail-grid">
        <app-detail-field label="ID Interno">{{ emp.idInterno }}</app-detail-field>
        <app-detail-field label="Nombre Completo">{{ emp.nombreCompleto }}</app-detail-field>
        <app-detail-field label="NSS">{{ emp.nss || '—' }}</app-detail-field>
        <app-detail-field label="RFC" [mono]="true">{{ emp.rfc || '—' }}</app-detail-field>
        <app-detail-field label="CURP" [mono]="true">{{ emp.curp || '—' }}</app-detail-field>
        <app-detail-field label="Sexo">{{ emp.sexo || '—' }}</app-detail-field>
        <app-detail-field label="Estado Civil">{{ emp.estadoCivil || '—' }}</app-detail-field>
        <app-detail-field label="Fecha de Ingreso">{{
          emp.fechaIngreso ? (emp.fechaIngreso | date: 'dd/MM/yyyy') : '—'
        }}</app-detail-field>
        <app-detail-field label="Lugar de Nacimiento">{{
          emp.lugarNacimiento || '—'
        }}</app-detail-field>
        <app-detail-field label="Nacionalidad">{{ emp.nacionalidad || '—' }}</app-detail-field>
      </div>
    </div>

    <!-- ═══ Datos Laborales ═══ -->
    @if (emp.datosLaborales; as dl) {
      <div class="form-section">
        <intelishs-section-header title="Datos Laborales" />
        <div class="detail-grid">
          <app-detail-field label="Dirección">{{ dl.direccion || '—' }}</app-detail-field>
          <app-detail-field label="Área">{{ dl.area || '—' }}</app-detail-field>
          <app-detail-field label="Puesto">{{ dl.puesto || '—' }}</app-detail-field>
          <app-detail-field label="Periodo de Pago">{{ dl.periodoPago || '—' }}</app-detail-field>
          <app-detail-field label="Esquema de Pago">{{ dl.esquemaPago || '—' }}</app-detail-field>
          <app-detail-field label="Tipo de Contrato">{{ dl.tipoContrato || '—' }}</app-detail-field>
          <app-detail-field label="Duración de Contrato">{{
            dl.duracionContrato || '—'
          }}</app-detail-field>
          <app-detail-field label="Horario de Trabajo">{{
            dl.horarioTrabajo || '—'
          }}</app-detail-field>
          <app-detail-field label="Días de Descanso">{{ dl.diasDescanso || '—' }}</app-detail-field>
        </div>
        @if (dl.actividades) {
          <app-detail-field label="Actividades" [full]="true">{{
            dl.actividades
          }}</app-detail-field>
        }
      </div>
    }

    <!-- ═══ Datos Salariales ═══ -->
    @if (emp.datosSalariales; as ds) {
      <div class="form-section">
        <intelishs-section-header title="Datos Salariales" />
        <div class="detail-grid">
          <app-detail-field label="SD Nominal" [money]="true">{{
            ds.sdNominal != null ? (ds.sdNominal | number: '1.2-2') : '—'
          }}</app-detail-field>
          <app-detail-field label="SD Integrado" [money]="true">{{
            ds.sdIntegrado != null ? (ds.sdIntegrado | number: '1.2-2') : '—'
          }}</app-detail-field>
          <app-detail-field label="Sueldo Neto IMSS" [money]="true">{{
            ds.sueldoNetoImss != null ? (ds.sueldoNetoImss | number: '1.2-2') : '—'
          }}</app-detail-field>
        </div>
      </div>
    }

    <!-- ═══ Domicilio ═══ -->
    @if (emp.domicilio; as dom) {
      <div class="form-section">
        <intelishs-section-header title="Domicilio" />
        <div class="detail-grid">
          <app-detail-field label="Calle">{{ dom.calle || '—' }}</app-detail-field>
          <app-detail-field label="No. Ext / Int"
            >{{ dom.numeroExt || '—' }}
            {{ dom.numeroInt ? '/ ' + dom.numeroInt : '' }}</app-detail-field
          >
          <app-detail-field label="Colonia">{{ dom.colonia || '—' }}</app-detail-field>
          <app-detail-field label="Población / Municipio">{{
            dom.poblacion || '—'
          }}</app-detail-field>
          <app-detail-field label="Estado">{{ dom.estado || '—' }}</app-detail-field>
          <app-detail-field label="Código Postal" [mono]="true">{{
            dom.codigoPostal || '—'
          }}</app-detail-field>
          <app-detail-field label="Teléfono">{{ dom.telefono || '—' }}</app-detail-field>
          <app-detail-field label="Email">{{ dom.email || '—' }}</app-detail-field>
        </div>
      </div>
    }

    <!-- ═══ Datos Bancarios ═══ -->
    @if (emp.datosBancarios; as db) {
      <div class="form-section">
        <intelishs-section-header title="Datos Bancarios" />
        <div class="detail-grid">
          <app-detail-field label="Banco">{{ db.banco || '—' }}</app-detail-field>
          <app-detail-field label="Número de Cuenta" [mono]="true">{{
            db.numeroCuenta || '—'
          }}</app-detail-field>
          <app-detail-field label="CLABE Interbancaria" [mono]="true">{{
            db.clabeInterbancaria || '—'
          }}</app-detail-field>
        </div>
      </div>
    }

    <!-- ═══ INFONAVIT ═══ -->
    @if (emp.infonavit; as inf) {
      <div class="form-section">
        <intelishs-section-header title="INFONAVIT" />
        <div class="detail-grid">
          <app-detail-field label="Número de Crédito">{{
            inf.numeroCredito || '—'
          }}</app-detail-field>
          <app-detail-field label="Tipo de Retención">{{
            inf.tipoRetencion || '—'
          }}</app-detail-field>
          <app-detail-field label="Importe / VSM / Cuota">{{
            inf.importeVsmCuota || '—'
          }}</app-detail-field>
        </div>
      </div>
    }

    <!-- ═══ Pagadoras IMSS ═══ -->
    @if (emp.pagadoras && emp.pagadoras.length > 0) {
      <div class="form-section">
        <intelishs-section-header title="Pagadoras IMSS" />
        @for (p of emp.pagadoras; track $index) {
          <div class="detail-array-row">
            <app-detail-field label="Pagadora" size="sm">{{ p.pagadora || '—' }}</app-detail-field>
            <app-detail-field label="Fecha Alta IMSS" size="sm">{{
              p.fechaAltaImss ? (p.fechaAltaImss | date: 'dd/MM/yyyy') : '—'
            }}</app-detail-field>
            <app-detail-field label="Fecha Baja IMSS" size="sm">{{
              p.fechaBajaImss ? (p.fechaBajaImss | date: 'dd/MM/yyyy') : '—'
            }}</app-detail-field>
            <!-- Un booleano no necesita pastilla: punto de color y texto, el
                 mismo recurso con el que se marca el estado en toda la app. -->
            <app-detail-field label="Actual" size="sm">
              <intelishs-status-text [tone]="p.esActual ? 'ok' : 'neutro'">
                {{ p.esActual ? 'Sí' : 'No' }}
              </intelishs-status-text>
            </app-detail-field>
          </div>
        }
      </div>
    }

    <!-- ═══ Contactos de Emergencia ═══ -->
    @if (emp.contactosEmergencia && emp.contactosEmergencia.length > 0) {
      <div class="form-section">
        <intelishs-section-header title="Contactos de Emergencia" />
        @for (c of emp.contactosEmergencia; track $index) {
          <div class="detail-array-row">
            <app-detail-field label="Nombre" size="sm">{{ c.nombre }}</app-detail-field>
            <app-detail-field label="Parentesco" size="sm">{{
              c.parentesco || '—'
            }}</app-detail-field>
            <app-detail-field label="Teléfono" size="sm">{{ c.telefono || '—' }}</app-detail-field>
          </div>
        }
      </div>
    }

    <!-- ═══ Estudios ═══ -->
    @if (emp.estudios && emp.estudios.length > 0) {
      <div class="form-section">
        <intelishs-section-header title="Estudios" />
        @for (e of emp.estudios; track $index) {
          <div class="detail-array-row">
            <app-detail-field label="Grado de Estudios" size="sm">{{
              e.gradoEstudios || '—'
            }}</app-detail-field>
            <app-detail-field label="Campo de Estudio" size="sm">{{
              e.campoEstudio || '—'
            }}</app-detail-field>
          </div>
        }
      </div>
    }
  </div>
}
